<template>
  <div
    :data-tint="tint"
    class=""
    :class="[
      mainSize,
      'avatar-shape',
      shadowColorComputed,
      flat ? '' : shadowColor ? 'shadow-md' : 'shadow-xl'
    ]"
  >
    <div class="relative">
      <div
        class="from-primary-500 to-primary-600 text-contrast-primary relative overflow-hidden bg-linear-to-bl font-bold ring-1"
        :class="[
          'avatar-shape',
          mainSize,
          fontSize,
          ringColorComputed,
          borderColorComputed,
          borderSizeComputed
        ]"
      >
        <span
          v-if="!imgUrl || !imgAvailable"
          class="absolute inset-0 flex items-center justify-center"
          :class="[blinkIconEnabled ? 'animate-fadeoutfadein' : '']"
        >
          <span v-if="!icon">
            {{ initials }}
          </span>
          <FontAwesomeIcon v-else :icon="icon" />
        </span>
        <img
          v-else
          class="absolute inset-0 block h-full w-full items-center justify-center bg-white object-cover"
          :class="[blinkIconEnabled ? 'animate-fadeoutfadein' : '']"
          :src="imgUrl"
          :alt="initials"
          @error="imgNotAvailable"
        />
        <span
          v-if="blinkIconEnabled"
          class="absolute inset-0 flex items-center justify-center"
          :class="[blinkIconEnabled ? 'animate-fadeinfadeout' : '']"
        >
          <FontAwesomeIcon :icon="blinkIconComputed" />
        </span>
      </div>
      <div
        v-if="!hideStatus"
        class="absolute right-0 bottom-0 rounded-full ring-white"
        :class="[statusSize, statusColorComputed]"
      ></div>
      <div
        v-if="secondIconComputed"
        class="absolute bottom-0 left-0 flex items-center justify-center rounded-full bg-white ring-white"
        :class="[secondIconSize]"
      >
        <FontAwesomeIcon
          :icon="secondIconComputed"
          :class="[secondIconColorComputed, secondIconTextSize]"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'BaseAvatar',
  components: { FontAwesomeIcon },
  props: {
    initials: {
      type: String,
      required: false,
      default: null
    },
    icon: {
      type: Array,
      required: false,
      default: null
    },
    imgUrl: {
      type: String,
      required: false,
      default: null
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
      validator: (value) =>
        ['extra-small', 'small', 'medium', 'large', 'extra-large'].indexOf(
          value
        ) !== -1
    },
    ringColor: {
      type: String,
      required: false,
      default: null
    },
    borderColor: {
      type: String,
      required: false,
      default: null
    },
    shadowColor: {
      type: String,
      required: false,
      default: null
    },
    hideStatus: {
      type: Boolean,
      required: false,
      default: false
    },
    statusColor: {
      type: String,
      required: false,
      default: null
    },
    blinkIconEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    blinkIcon: {
      type: Array,
      required: false,
      default: () => ['fal', 'times']
    },
    secondIcon: {
      type: [Array, String],
      required: false,
      default: null
    },
    secondIconColor: {
      type: String,
      required: false,
      default: null
    },
    tint: {
      type: String,
      required: false,
      default: null
    },
    flat: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      imgAvailable: true
    }
  },
  computed: {
    mainSize() {
      switch (this.size) {
        case 'extra-small':
          return 'w-6 h-6'
        case 'small':
          return 'w-8 h-8'
        case 'large':
          return 'w-10 h-10'
        case 'extra-large':
          return 'w-12 h-12'
        case 'huge':
          return 'w-24 h-24'
        default:
          return 'w-9 h-9'
      }
    },
    fontSize() {
      switch (this.size) {
        case 'extra-small':
          return 'text-xxs'
        case 'small':
          return 'text-xs'
        case 'large':
          return 'text-base'
        case 'extra-large':
          return 'text-xl'
        case 'huge':
          return 'text-4xl'
        default:
          return 'text-sm'
      }
    },
    ringColorComputed() {
      return this.ringColor ?? 'ring-slate-300'
    },
    borderColorComputed() {
      return this.borderColor ?? 'border-white'
    },
    borderSizeComputed() {
      switch (this.size) {
        case 'extra-small':
          return 'border'
        case 'large':
          return 'border-[3px]'
        case 'extra-large':
          return 'border-4'
        case 'huge':
          return 'border-6'
        case 'small':
        default:
          return 'border-2'
      }
    },
    statusSize() {
      switch (this.size) {
        case 'extra-small':
          return 'w-1.5 h-1.5 ring-1'
        case 'small':
          return 'w-1.5 h-1.5 ring-2'
        case 'large':
          return 'w-2.5 h-2.5 ring-2'
        case 'extra-large':
          return 'w-3 h-3 ring-3'
        case 'huge':
          return 'w-5 h-5 ring-5'
        default:
          return 'w-2 h-2 ring-2'
      }
    },
    secondIconSize() {
      switch (this.size) {
        case 'extra-small':
          return 'w-1.5 h-1.5 ring-1'
        case 'small':
          return 'w-1.5 h-1.5 ring-2'
        case 'large':
          return 'w-2.5 h-2.5 ring-2'
        case 'extra-large':
          return 'w-3 h-3 ring-3'
        case 'huge':
          return 'w-5 h-5 ring-5'
        default:
          return 'w-2 h-2 ring-2'
      }
    },
    statusColorComputed() {
      return this.statusColor ?? 'bg-slate-600'
    },
    shadowColorComputed() {
      return this.shadowColor ?? ''
    },
    blinkIconComputed() {
      return Array.isArray(this.blinkIcon)
        ? this.blinkIcon
        : ['fal', this.blinkIcon]
    },
    secondIconComputed() {
      if (!this.secondIcon) return null
      return Array.isArray(this.secondIcon)
        ? this.secondIcon
        : ['fas', this.secondIcon]
    },
    secondIconColorComputed() {
      return this.secondIconColor ?? 'text-slate-600'
    },
    secondIconTextSize() {
      switch (this.size) {
        case 'extra-small':
        case 'small':
          return 'text-[6px]'
        case 'large':
          return 'text-[9px]'
        case 'extra-large':
          return 'text-[12px]'
        case 'huge':
          return 'text-[20px]'
        default:
          return 'text-[7px]'
      }
    }
  },
  methods: {
    imgNotAvailable() {
      this.imgAvailable = false
    }
  }
}
</script>
